import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

/**
 * Gabarit commun aux pages d'aide (Livraison, Retours, Tailles, FAQ, Contact).
 * Reprend strictement la typographie et les espacements SUNSET.
 */
export function HelpPage({
  eyebrow = "Aide",
  title,
  intro,
  children,
}: {
  eyebrow?: string;
  title: string;
  intro?: string;
  children: ReactNode;
}) {
  return (
    <main className="mx-auto w-full max-w-3xl px-6 pb-20 pt-28 sm:px-8 md:pb-28 md:pt-36">
      <nav aria-label="Fil d'Ariane" className="label-xs text-muted-foreground">
        <Link to="/" className="transition-colors hover:text-foreground">
          Accueil
        </Link>
        <span className="mx-2 text-border">/</span>
        <span className="text-foreground">{title}</span>
      </nav>

      <header className="mt-8 border-b border-border pb-10">
        <p className="label-xs text-accent">{eyebrow}</p>
        <h1 className="mt-4 font-serif text-4xl font-light leading-tight tracking-tight sm:text-5xl">{title}</h1>
        {intro ? (
          <p className="mt-6 max-w-2xl text-sm leading-[1.9] text-muted-foreground sm:text-base">{intro}</p>
        ) : null}
      </header>

      <div className="mt-12 space-y-12">{children}</div>
    </main>
  );
}

export function HelpSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2 className="font-serif text-xl leading-snug tracking-tight sm:text-2xl">{title}</h2>
      <div className="mt-4 space-y-3 text-sm leading-[1.9] text-muted-foreground sm:text-base">{children}</div>
    </section>
  );
}

/** Encadré discret pour les renvois vers une autre page d'aide. */
export function HelpNote({ children }: { children: ReactNode }) {
  return (
    <aside className="border border-border bg-sand/30 p-6 text-sm leading-[1.9] text-muted-foreground sm:p-8">
      {children}
    </aside>
  );
}

export function HelpLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="link-underline text-foreground transition-colors hover:text-accent">
      {children}
    </Link>
  );
}
